import { useFormContext } from '@calendly/form';
import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import classNames from 'classnames';
import { useCallback, forwardRef } from 'react';
import { useBooleanState } from 'hooks/useBooleanState';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { AddLocation } from './add_location';
import { MAX_LOCATIONS } from './constants';
import { DependentNotice } from './dependent_notice';
import { useLocationManagement, useLocationSettings } from './hooks';
import { useAvailableKinds } from './hooks/use_availabile_kinds';
import { LocationList } from './location_list';
import { LocationSelect, LocationSelectButtons } from './location_select';
import { getIsRROrCRRWithoutCollectivePools, getUserPools } from './utils';
const fieldsetCss = "fx3nq9z";
const Fieldset = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(fieldsetCss, props.className)} />;
});
const editableNoticeCss = "e1dqg27o";
const EditableNotice = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(editableNoticeCss, props.className)} />;
});
const containerNoLocationsCss = "cfelvrl";
const containerCss = "czd7w8n";
const Container = forwardRef((props, ref) => {
  const {
    noLocations,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerNoLocationsCss]: noLocations
  }, props.className)} />;
});
export const Location = ({
  eventType,
  membership,
  conferencingOrganizer,
  isDisabled = false,
  allowMultiple = false,
  isLocked = false,
  isManaged,
  withIntegrationErrors = true,
  hideErrors = false,
  isReadOnly,
  startExpanded,
  ...props
}) => {
  const {
    values
  } = useFormContext();
  const {
    isGroup,
    isPoll
  } = eventType;
  const {
    isRoundRobin,
    isCollectiveRoundRobin,
    isCollective
  } = useETEditorFormikPoolingType();
  const {
    filteredLocations,
    addLocation
  } = useLocationManagement(eventType, membership);
  const noLocations = filteredLocations.length === 0;
  const userPools = getUserPools(values);
  const conferenceOrganizer = conferencingOrganizer || eventType;
  const [isLocationSelectVisible, showLocationSelect, hideLocationSelect] = useBooleanState(noLocations);
  const [possibleKinds, showIntegrationErrors] = useLocationSettings(eventType, userPools, withIntegrationErrors, isManaged, hideErrors);
  const availableKinds = useAvailableKinds(possibleKinds, filteredLocations);
  const multipleLocationsAdded = filteredLocations.length > 1;
  const maxLocationsAdded = filteredLocations.length >= MAX_LOCATIONS;
  const canAddMoreLocations = allowMultiple && !maxLocationsAdded && !isLocationSelectVisible && !isReadOnly;
  const firstLocation = filteredLocations[0];
  const showDependentNotice = isCollective && firstLocation && ['google_conference', 'microsoft_teams_conference'].includes(firstLocation.kind);
  const shouldDisableOutbound = isGroup || isPoll;
  const isRROrCRRWithoutCollectivePools = getIsRROrCRRWithoutCollectivePools(isRoundRobin, isCollectiveRoundRobin, userPools);
  useOnValueChange(filteredLocations, () => {
    if (filteredLocations.length === 0) {
      showLocationSelect();
    } else if (!allowMultiple && filteredLocations.length > 0) {
      hideLocationSelect();
    }
  });
  const handleAddLocation = useCallback(kind => {
    addLocation(kind, allowMultiple);
    hideLocationSelect();
  }, [addLocation, allowMultiple, hideLocationSelect]);
  return <Fieldset {...props}>
      {isManaged && eventType.id && <EditableNotice>
        {I18n.t('publisher.organization.managed_event_types.location_is_editable')}
      </EditableNotice>}
      <Container noLocations={noLocations}>
        <LocationList id="location-list" isDisabled={isDisabled} isLocked={isLocked} possibleKinds={possibleKinds} showIntegrationErrors={showIntegrationErrors} membership={membership} eventType={eventType} conferenceOrganizer={conferenceOrganizer} isReadOnly={isReadOnly} />
        {isLocationSelectVisible && !isReadOnly && (filteredLocations.length > 0 || isRROrCRRWithoutCollectivePools ? <LocationSelect onChange={handleAddLocation} availableKinds={availableKinds} isDisabled={isLocked || isDisabled} startExpanded={startExpanded} /> : <LocationSelectButtons onChange={handleAddLocation} availableKinds={availableKinds} shouldDisableOutbound={shouldDisableOutbound} />)}
      </Container>
      {showDependentNotice && <DependentNotice />}
      {canAddMoreLocations && <AddLocation multipleLocationsAdded={multipleLocationsAdded} onClick={showLocationSelect} />}
    </Fieldset>;
};

require("./location.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./location.js");